import { useState, useEffect } from 'react'
import TenantList from './TenantList'
import TenantForm from './TenantForm'
import UserManagement from './UserManagement'
import AdminUserManagement from './AdminUserManagement'
import JarixMetricsDashboard from './JarixMetricsDashboard'
import BillingDashboard from './BillingDashboard'
import BillingEditModal from './BillingEditModal'
import ServiceCatalog from './ServiceCatalog'
import TenantServices from './TenantServices'
import RevenueDashboard from './RevenueDashboard'
import ClientList from './ClientList'
import ClientForm from './ClientForm'
import ClientServices from './ClientServices'
import { api } from '../../services/api'
import type { Tenant, TenantSummary, AdminPermissions, ClientFormData } from '../../services/api'

export type { Tenant }

type AdminTab = 'metrics' | 'clients' | 'ps-clients' | 'services' | 'billing'
type ServicesSubTab = 'catalog' | 'revenue'

// Professional Services Client type for local state
interface PSClient {
  id: string
  name: string
  slug: string
  linkedTenantId: string | null
  linkedTenant: { id: string; name: string; slug: string } | null
  status: 'active' | 'inactive' | 'prospect'
  clientType: 'professional' | 'hybrid'
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  legalName: string | null
  taxId: string | null
  billingEmails: string[]
  address: string | null
  paymentTerms: 'net_15' | 'net_30' | 'net_45' | 'net_60'
  currency: string
  notes: string | null
}

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<AdminTab>('metrics')
  const [servicesSubTab, setServicesSubTab] = useState<ServicesSubTab>('catalog')
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null)
  const [managingUsersTenant, setManagingUsersTenant] = useState<Tenant | null>(null)
  const [managingServicesTenant, setManagingServicesTenant] = useState<Tenant | null>(null)
  const [editingBillingTenant, setEditingBillingTenant] = useState<TenantSummary | null>(null)
  const [showAdminUserManagement, setShowAdminUserManagement] = useState(false)
  const [permissions, setPermissions] = useState<AdminPermissions | null>(null)

  // Professional Services Clients state
  const [showPSClientForm, setShowPSClientForm] = useState(false)
  const [editingPSClient, setEditingPSClient] = useState<PSClient | null>(null)
  const [managingPSClientServices, setManagingPSClientServices] = useState<PSClient | null>(null)
  const [psClientRefreshTrigger, setPsClientRefreshTrigger] = useState(0)

  // Fetch tenants from API with auth
  const fetchTenants = async () => {
    try {
      setLoading(true)
      setError(null)

      const result = await api.listTenants()

      if (!result.success) {
        // Check if it's an auth error
        if (result.error?.includes('401') || result.error?.includes('Unauthorized')) {
          setError('Sesión expirada. Por favor, vuelve a iniciar sesión.')
        } else if (result.error?.includes('403') || result.error?.includes('Forbidden')) {
          setError('No tienes permisos para acceder al portal de administración.')
        } else {
          throw new Error(result.error || 'Error al cargar clientes')
        }
        return
      }

      setTenants(result.data?.tenants || [])
    } catch (err) {
      console.error('Error fetching tenants:', err)
      setError('Error al cargar clientes. Usando datos de ejemplo.')
      // For now, use mock data if API fails
      setTenants([
        {
          id: '1',
          name: 'Bullkservices',
          slug: 'bullkservices',
          status: 'active',
          plan: 'enterprise',
          crowdstrike_client_id: '1ff0e9495a564193bead913573b73b79',
          crowdstrike_region: 'eu-1',
          jumpcloud_org_id: '646f7bb4a6308a8bd35fff0f',
          primary_color: '#FF9B71',
          created_at: '2024-10-30T00:00:00Z',
          updated_at: '2024-10-30T00:00:00Z'
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  // Load user permissions on mount
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const result = await api.getMe()
        if (result.success && result.data?.user?.permissions) {
          setPermissions(result.data.user.permissions)
        }
      } catch (err) {
        console.error('Error loading permissions:', err)
      }
    }
    loadPermissions()
  }, [])

  useEffect(() => {
    fetchTenants()
  }, [])

  const handleAddTenant = () => {
    setEditingTenant(null)
    setShowForm(true)
  }

  const handleEditTenant = (tenant: Tenant) => {
    setEditingTenant(tenant)
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingTenant(null)
  }

  const handleSaveTenant = async (tenantData: Partial<Tenant>) => {
    try {
      let result
      if (editingTenant) {
        result = await api.updateTenant(editingTenant.id, tenantData)
      } else {
        result = await api.createTenant(tenantData)
      }

      if (!result.success) {
        throw new Error(result.error || 'Error al guardar cliente')
      }

      await fetchTenants()
      handleCloseForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    }
  }

  const handleDeleteTenant = async (tenantId: string) => {
    if (!confirm('¿Estas seguro de eliminar este cliente? Esta accion no se puede deshacer.')) {
      return
    }

    try {
      const result = await api.deleteTenant(tenantId)

      if (!result.success) {
        throw new Error(result.error || 'Error al eliminar cliente')
      }

      await fetchTenants()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar')
    }
  }

  const handleManageUsers = (tenant: Tenant) => {
    setManagingUsersTenant(tenant)
  }

  const handleCloseUserManagement = () => {
    setManagingUsersTenant(null)
  }

  const handleManageServices = (tenant: Tenant) => {
    setManagingServicesTenant(tenant)
  }

  const handleCloseServicesManagement = () => {
    setManagingServicesTenant(null)
  }

  const handleEditBilling = (tenant: TenantSummary) => {
    setEditingBillingTenant(tenant)
  }

  const handleCloseBillingEdit = () => {
    setEditingBillingTenant(null)
  }

  const handleSaveBilling = async (tenantId: string, billingData: {
    legalName: string
    taxId: string
    billingAddress: string
    billingPostalCode: string
    billingCountry: string
    billingEmails: string[]
    paymentTerms: string
    billingDay: number
    prorationEnabled: boolean
  }) => {
    try {
      const result = await api.updateTenant(tenantId, {
        legal_name: billingData.legalName,
        tax_id: billingData.taxId,
        billing_address: billingData.billingAddress,
        billing_postal_code: billingData.billingPostalCode,
        billing_country: billingData.billingCountry,
        billingEmails: billingData.billingEmails, // Backend expects array, converts to JSON
        payment_terms: billingData.paymentTerms,
        billing_day: billingData.billingDay,
        proration_enabled: billingData.prorationEnabled,
      })

      if (!result.success) {
        throw new Error(result.error || 'Error al guardar datos de facturacion')
      }

      handleCloseBillingEdit()
      // Refresh will be handled by the BillingDashboard component
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    }
  }

  // Professional Services Clients handlers
  const handleAddPSClient = () => {
    setEditingPSClient(null)
    setShowPSClientForm(true)
  }

  const handleEditPSClient = (client: PSClient) => {
    setEditingPSClient(client)
    setShowPSClientForm(true)
  }

  const handleClosePSClientForm = () => {
    setShowPSClientForm(false)
    setEditingPSClient(null)
  }

  const handleSavePSClient = async (clientData: ClientFormData) => {
    try {
      let result
      if (editingPSClient) {
        result = await api.updateClient(editingPSClient.id, clientData)
      } else {
        result = await api.createClient(clientData)
      }

      if (!result.success) {
        throw new Error(result.error || 'Error al guardar cliente')
      }

      setPsClientRefreshTrigger(prev => prev + 1)
      handleClosePSClientForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    }
  }

  const handleDeletePSClient = (_clientId: string) => {
    // ClientList handles the actual deletion, we just refresh the list
    setPsClientRefreshTrigger(prev => prev + 1)
  }

  const handleManagePSClientServices = (client: PSClient) => {
    setManagingPSClientServices(client)
  }

  const handleClosePSClientServices = () => {
    setManagingPSClientServices(null)
    setPsClientRefreshTrigger(prev => prev + 1)
  }

  return (
    <div className="p-6">
      {/* Header - Updated with Jarix branding */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-white tracking-tight">Portal de Administracion</h1>
            <p className="text-gray-400 mt-1 font-sans">
              Gestiona los clientes y sus configuraciones de seguridad
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Admin Users Button - only for users who can create users */}
            {permissions?.canCreateUsers && (
              <button
                onClick={() => setShowAdminUserManagement(true)}
                className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-button transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Usuarios Admin
              </button>
            )}
            {/* Add Client Button - only for users who can edit */}
            {activeTab === 'clients' && permissions?.canEditUsers && (
              <button
                onClick={handleAddTenant}
                className="bg-jarix-coral hover:bg-jarix-coral-dark text-white font-semibold py-2 px-4 rounded-button transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Agregar Cliente
              </button>
            )}
            {/* Add PS Client Button - only for users who can edit */}
            {activeTab === 'ps-clients' && permissions?.canEditUsers && (
              <button
                onClick={handleAddPSClient}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-button transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nuevo Cliente PS
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs - Updated with Jarix styling */}
      <div className="flex gap-1 mb-6 bg-gray-800 p-1 rounded-card-sm w-fit">
        <button
          onClick={() => setActiveTab('metrics')}
          className={`px-4 py-2 rounded-button text-sm font-medium transition-colors ${
            activeTab === 'metrics'
              ? 'bg-jarix-coral text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Métricas Globales
          </span>
        </button>
        <button
          onClick={() => setActiveTab('clients')}
          className={`px-4 py-2 rounded-button text-sm font-medium transition-colors ${
            activeTab === 'clients'
              ? 'bg-jarix-coral text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Seguridad Gestionada
          </span>
        </button>
        <button
          onClick={() => setActiveTab('ps-clients')}
          className={`px-4 py-2 rounded-button text-sm font-medium transition-colors ${
            activeTab === 'ps-clients'
              ? 'bg-purple-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Servicios Profesionales
          </span>
        </button>
        <button
          onClick={() => setActiveTab('services')}
          className={`px-4 py-2 rounded-button text-sm font-medium transition-colors ${
            activeTab === 'services'
              ? 'bg-jarix-coral text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            Servicios
          </span>
        </button>
        {/* Billing Tab - only visible if user has billing access */}
        {permissions?.canAccessBilling && (
          <button
            onClick={() => setActiveTab('billing')}
            className={`px-4 py-2 rounded-button text-sm font-medium transition-colors ${
              activeTab === 'billing'
                ? 'bg-jarix-coral text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
              </svg>
              FI
            </span>
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">
          {error}
          <button
            onClick={() => setError(null)}
            className="float-right text-red-400 hover:text-red-300"
          >
            &times;
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'metrics' && (
        <JarixMetricsDashboard />
      )}

      {activeTab === 'clients' && (
        <>
          {/* Stats Cards - Updated with Jarix styling */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-800 rounded-card-sm p-4 border border-gray-700/50">
              <p className="text-gray-400 text-sm font-sans">Total Clientes</p>
              <p className="text-2xl font-heading font-semibold text-white">{tenants.length}</p>
            </div>
            <div className="bg-gray-800 rounded-card-sm p-4 border border-gray-700/50">
              <p className="text-gray-400 text-sm font-sans">Activos</p>
              <p className="text-2xl font-heading font-semibold text-green-400">
                {tenants.filter(t => t.status === 'active').length}
              </p>
            </div>
            <div className="bg-gray-800 rounded-card-sm p-4 border border-gray-700/50">
              <p className="text-gray-400 text-sm font-sans">En Trial</p>
              <p className="text-2xl font-heading font-semibold text-yellow-400">
                {tenants.filter(t => t.status === 'trial').length}
              </p>
            </div>
            <div className="bg-gray-800 rounded-card-sm p-4 border border-gray-700/50">
              <p className="text-gray-400 text-sm font-sans">Suspendidos</p>
              <p className="text-2xl font-heading font-semibold text-red-400">
                {tenants.filter(t => t.status === 'suspended').length}
              </p>
            </div>
          </div>

          {/* Tenant List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jarix-coral mx-auto mb-4"></div>
              <p className="text-gray-400">Cargando clientes...</p>
            </div>
          ) : (
            <TenantList
              tenants={tenants}
              onEdit={permissions?.canEditUsers ? handleEditTenant : undefined}
              onDelete={permissions?.canDeleteUsers ? handleDeleteTenant : undefined}
              onManageUsers={permissions?.canEditUsers ? handleManageUsers : undefined}
              onManageServices={permissions?.canEditUsers ? handleManageServices : undefined}
              isReadOnly={!permissions?.canEditUsers}
            />
          )}
        </>
      )}

      {activeTab === 'ps-clients' && (
        <>
          <div className="mb-6">
            <p className="text-gray-400">
              Clientes de servicios profesionales (consultoria, implementaciones, assessments, etc.)
            </p>
          </div>
          <ClientList
            onEdit={permissions?.canEditUsers ? handleEditPSClient : undefined}
            onDelete={permissions?.canDeleteUsers ? handleDeletePSClient : undefined}
            onManageServices={permissions?.canEditUsers ? handleManagePSClientServices : undefined}
            isReadOnly={!permissions?.canEditUsers}
            refreshTrigger={psClientRefreshTrigger}
          />
        </>
      )}

      {activeTab === 'services' && (
        <>
          {/* Services Sub-tabs */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setServicesSubTab('catalog')}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                servicesSubTab === 'catalog'
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Catalogo de Servicios
            </button>
            <button
              onClick={() => setServicesSubTab('revenue')}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                servicesSubTab === 'revenue'
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Revenue & Analytics
            </button>
          </div>

          {/* Quick Actions */}
          {servicesSubTab === 'catalog' && tenants.length > 0 && (
            <div className="bg-gray-800 rounded-xl p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Asignar Servicio a Cliente</h3>
              <div className="flex flex-wrap gap-2">
                {tenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => handleManageServices(tenant)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: tenant.primary_color }}
                    />
                    {tenant.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {servicesSubTab === 'catalog' && <ServiceCatalog />}
          {servicesSubTab === 'revenue' && <RevenueDashboard />}
        </>
      )}

      {activeTab === 'billing' && (
        <BillingDashboard onEditBilling={handleEditBilling} />
      )}

      {/* Tenant Form Modal */}
      {showForm && (
        <TenantForm
          tenant={editingTenant}
          onSave={handleSaveTenant}
          onClose={handleCloseForm}
        />
      )}

      {/* User Management Modal */}
      {managingUsersTenant && (
        <UserManagement
          tenantId={managingUsersTenant.id}
          tenantName={managingUsersTenant.name}
          onClose={handleCloseUserManagement}
        />
      )}

      {/* Billing Edit Modal */}
      {editingBillingTenant && (
        <BillingEditModal
          tenant={editingBillingTenant}
          onSave={handleSaveBilling}
          onClose={handleCloseBillingEdit}
        />
      )}

      {/* Tenant Services Modal */}
      {managingServicesTenant && (
        <TenantServices
          tenantId={managingServicesTenant.id}
          tenantName={managingServicesTenant.name}
          onClose={handleCloseServicesManagement}
        />
      )}

      {/* Admin User Management Modal */}
      {showAdminUserManagement && (
        <AdminUserManagement
          onClose={() => setShowAdminUserManagement(false)}
        />
      )}

      {/* Professional Services Client Form Modal */}
      {showPSClientForm && (
        <ClientForm
          client={editingPSClient}
          onSave={handleSavePSClient}
          onClose={handleClosePSClientForm}
        />
      )}

      {/* Professional Services Client Services Modal */}
      {managingPSClientServices && (
        <ClientServices
          clientId={managingPSClientServices.id}
          clientName={managingPSClientServices.name}
          onClose={handleClosePSClientServices}
        />
      )}
    </div>
  )
}
